import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../api/adminApi';
import { 
  UsersIcon, 
  BuildingStorefrontIcon, 
  CurrencyRupeeIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import PremiumTable from '../components/common/PremiumTable';

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [statsRes, usersRes, chartRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getRecentUsers(5),
        adminApi.getGrowthChart()
      ]);
      
      setStats(statsRes.data.data);
      setRecentUsers(usersRes.data.data);
      setChartData(chartRes.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const statCards = [
    { title: 'Total Users', value: stats?.totalUsers || 0, icon: UsersIcon, color: 'bg-blue-500', change: '+12%' },
    { title: 'Total Businesses', value: stats?.totalBusinesses || 0, icon: BuildingStorefrontIcon, color: 'bg-green-500', change: '+8%' },
    { title: 'Total Transactions', value: `₹${stats?.totalTransactionVolume || 0}`, icon: CurrencyRupeeIcon, color: 'bg-purple-500', change: '+15%' },
    { title: 'Total Outstanding', value: `₹${stats?.totalOutstanding || 0}`, icon: ChartBarIcon, color: 'bg-orange-500', change: '+5%' }
  ];

  const statusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex h-screen items-center justify-center bg-gray-900 text-white">Loading Admin Data...</div>;
  }

  return (
    <div className="min-h-screen bg-transparent">
      {/* Page Title & Actions */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Overview</h1>
          <p className="text-sm text-gray-500 mt-1">Here is the global platform summary.</p>
        </div>
        <button 
          onClick={handleLogout}
          className="flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition-colors shadow-sm"
        >
          <ArrowRightOnRectangleIcon className="mr-2 h-4 w-4" /> Secure Logout
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-8">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex flex-col">
              <div className="flex justify-between items-start mb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{stat.title}</p>
                <div className={`${stat.color.replace('bg-', 'bg-opacity-10 text-').replace('-500', '-600')} p-2 rounded-lg bg-opacity-10`}>
                  <stat.icon className={`h-5 w-5 ${stat.color.replace('bg-', 'text-')}`} />
                </div>
              </div>
              <p className="text-3xl font-extrabold text-gray-900 mb-2">{stat.value}</p>
              <div className="flex items-center">
                <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-md">{stat.change}</span>
                <span className="text-xs text-gray-400 ml-2">from last month</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">User Growth</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={2} />
                <Line type="monotone" dataKey="active" stroke="#22c55e" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Transaction Volume</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="volume" fill="#3b82f6" />
                <Bar dataKey="outstanding" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.08)] p-6 border-t-[6px] border-red-600">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-extrabold text-gray-900 dark:text-white">
              Recent Users
            </h3>
            <button
              onClick={() => navigate('/users')}
              className="text-sm font-bold text-red-600 hover:text-red-700 bg-red-50 px-4 py-2 rounded-full transition-colors"
            >
              View All Users &rarr;
            </button>
          </div>
          <PremiumTable 
            title=""
            data={recentUsers}
            columns={[
              {
                header: 'User',
                accessor: 'name',
                render: (u) => (
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center ring-2 ring-white shadow-sm">
                      <span className="text-sm font-bold text-red-600">
                        {u.name.charAt(0)}
                      </span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-bold text-gray-900">{u.name}</p>
                      <p className="text-xs font-medium text-gray-500">{u.email}</p>
                    </div>
                  </div>
                )
              },
              {
                header: 'Business',
                accessor: 'business.businessName',
                render: (u) => <span className="text-sm font-medium text-gray-900">{u.business?.businessName || 'N/A'}</span>
              },
              {
                header: 'Status',
                accessor: 'isActive',
                render: (u) => (
                  <span className={`px-3 py-1 inline-flex text-xs font-bold rounded-full border ${u.isActive ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                )
              },
              {
                header: 'Joined',
                accessor: 'createdAt',
                render: (u) => <span className="text-sm font-medium text-gray-500">{new Date(u.createdAt).toLocaleDateString()}</span>
              }
            ]}
            searchKeys={[]}
            primaryKey="_id"
            paginationMode="none"
          />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            System Status
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">API Server</span>
              <span className="flex items-center">
                <span className="h-2 w-2 bg-green-500 rounded-full mr-2"></span>
                <span className="text-xs text-gray-600 dark:text-gray-400">Operational</span>
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Database</span>
              <span className="flex items-center">
                <span className="h-2 w-2 bg-green-500 rounded-full mr-2"></span>
                <span className="text-xs text-gray-600 dark:text-gray-400">Connected</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
